import { useMemo } from 'react';
import { ExerciseMatcher, WorkoutParser, type Exercise } from '@tg/domain';
import { trpc } from './api';

/** База упражнений с индексом поиска и парсером — один раз на сессию */
export function useCatalog() {
  const q = trpc.catalog.exercises.useQuery(undefined, { staleTime: 10 * 60_000 });
  return useMemo(() => {
    const list = (q.data || []) as Exercise[];
    const byId = new Map(list.map((e) => [e.id, e]));
    const matcher = new ExerciseMatcher(list);
    return { list, ready: !!q.data, ex: (id: string) => byId.get(id), matcher, parser: new WorkoutParser(matcher) };
  }, [q.data]);
}
export type Catalog = ReturnType<typeof useCatalog>;

type Utils = ReturnType<typeof trpc.useUtils>;

/** saveExercise возвращает сырую строку БД — та же проекция, что и catalog.exercises на сервере. */
function rowToExercise(row: Record<string, any>): Exercise {
  return {
    id: row.id,
    ru: row.ru,
    en: row.en,
    groups: row.groups ?? [],
    equipment: row.equipment,
    bodyPart: row.bodyPart ?? undefined,
    target: row.target ?? undefined,
    secondary: row.secondary ?? undefined,
    pm: row.pm,
    units: row.units,
    aliases: row.aliases ?? undefined,
    own: !!row.trainerId,
    step: row.step ?? undefined,
    gif: row.gif,
    img: row.img,
    video: null,
    link: null,
  };
}

/** Добавленное/изменённое своё упражнение видно в базе сразу — без ожидания перезапроса всего списка (~1300 строк). */
export function upsertCatalogExercise(utils: Utils, row: Record<string, any>) {
  const e = rowToExercise(row);
  utils.catalog.exercises.setData(undefined, ((old: Exercise[] | undefined) => {
    if (!old) return old;
    const i = old.findIndex((x) => x.id === e.id);
    if (i < 0) return [...old, e];
    const copy = old.slice();
    /* Видео и ссылка тренера хранятся отдельно от карточки (setExVideo, setExLink) — сохранение карточки их не трогает */
    copy[i] = { ...e, video: old[i].video ?? null, link: old[i].link ?? null };
    return copy;
  }) as any);
}

export function removeCatalogExercise(utils: Utils, id: string) {
  utils.catalog.exercises.setData(undefined, ((old: Exercise[] | undefined) => old && old.filter((x) => x.id !== id)) as any);
}

/** Видео тренера к упражнению поменялось — в базе на клиенте сразу */
export function setCatalogVideo(utils: Utils, exId: string, video: Exercise['video']) {
  utils.catalog.exercises.setData(undefined, ((old: Exercise[] | undefined) => old && old.map((x) => (x.id === exId ? { ...x, video: video ?? null } : x))) as any);
}

/** Ссылка тренера на видео к упражнению поменялась — в базе на клиенте сразу */
export function setCatalogLink(utils: Utils, exId: string, link: Exercise['link']) {
  utils.catalog.exercises.setData(undefined, ((old: Exercise[] | undefined) => old && old.map((x) => (x.id === exId ? { ...x, link: link ?? null } : x))) as any);
}
